import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  CourseTrack,
  Lesson,
  LessonStatus,
  LessonWithStatus,
} from '../../shared/models';
import { AcademyNavigationService } from '../../services/academy-navigation.service';
import { hasCompletedLearningTrack } from '../../shared/course-track.utils';

/**
 * Sidebar displaying course metadata and clickable lesson list with completion status.
 */
@Component({
  selector: 'ut-course-content-sidebar',
  templateUrl: './course-content-sidebar.component.html',
  styleUrls: ['./course-content-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CourseContentSidebarComponent {
  @Input() courseTrack!: CourseTrack;
  @Input() currentLessonId: number | null = null;
  @Input() showSignUpConfirmation = false;
  @Input() quizActive = false;

  @Output() lessonClick = new EventEmitter<Lesson>();
  @Output() quizClick = new EventEmitter<void>();
  @Output() cycleSignUp = new EventEmitter<void>();
  @Output() cycleSignUpConfirm = new EventEmitter<void>();

  constructor(
    private academyNavigationService: AcademyNavigationService,
  ) {}

  get lessons(): LessonWithStatus[] {
    if (!this.courseTrack?.courses) return [];

    return this.courseTrack.courses.map((lesson) => ({
      ...lesson,
      status: this.getLessonStatus(lesson),
      isCurrentLesson: lesson.id === this.currentLessonId,
    }));
  }

  get totalLessons(): number {
    return this.courseTrack?.courses?.length || 0;
  }

  get totalEstimatedTime(): number | undefined {
    // Use the pre-calculated total from CourseTrack if available
    // This avoids recalculating on the frontend
    const total = this.courseTrack?.total_estimated_time_minutes;
    return total && total > 0 ? total : undefined;
  }

  get difficultyLevel(): string | undefined {
    return this.courseTrack?.difficulty_level;
  }

  onLessonClick(lesson: LessonWithStatus): void {
    if (lesson.status !== 'locked') {
      this.lessonClick.emit(lesson);
    }
  }

  isLessonClickable(lesson: LessonWithStatus): boolean {
    return lesson.status !== 'locked';
  }

  private getLessonStatus(lesson: Lesson): LessonStatus {
    if (lesson.id === this.currentLessonId) {
      return 'in-progress';
    }

    if (lesson.viewed_by_current_user) {
      return 'completed';
    }

    if (this.courseTrack?.user_has_failed_quiz) {
      return 'failed';
    }

    return 'available';
  }

  getLessonNumber(index: number): number {
    return index + 1;
  }

  getLessonAriaLabel(lesson: LessonWithStatus, index: number): string {
    const number = this.getLessonNumber(index);
    let label = `Lesson ${number}: ${lesson.title}`;
    if (lesson.status === 'completed') label += ', completed';
    else if (lesson.status === 'failed') label += ', failed';
    return label;
  }

  get hasQuiz(): boolean {
    return !!this.courseTrack?.has_quiz;
  }

  get isQuizLocked(): boolean {
    return this.hasQuiz && !this.courseTrack?.user_has_viewed_all_courses;
  }

  get isQuizAvailable(): boolean {
    return (
      this.hasQuiz
      && !!this.courseTrack?.user_has_viewed_all_courses
      && !this.courseTrack?.user_has_passed_quiz
    );
  }

  get quizStatus(): 'locked' | 'available' | 'passed' | 'failed' | null {
    if (!this.hasQuiz) return null;
    if (this.courseTrack?.user_has_passed_quiz) return 'passed';
    if (this.courseTrack?.user_has_failed_quiz && !this.courseTrack?.user_has_viewed_all_courses) return 'failed';
    if (this.isQuizAvailable) return 'available';
    if (this.isQuizLocked) return 'locked';
    return null;
  }

  get quizLabel(): string {
    switch (this.quizStatus) {
      case 'locked':
        return 'Quiz Locked';
      case 'available':
        return 'Take the Quiz';
      case 'passed':
        return 'Quiz Completed';
      case 'failed':
        return 'Quiz Failed';
      default:
        return 'Quiz';
    }
  }

  get quizHint(): string {
    switch (this.quizStatus) {
      case 'locked':
        return 'Complete all of the lessons first';
      case 'available':
        return 'Test your lesson knowledge';
      case 'passed':
        return 'You have finished this course';
      case 'failed':
        return 'Review the failed lessons to try again';
      default:
        return '';
    }
  }

  onQuizClick(): void {
    if (this.isQuizAvailable && !this.quizActive) {
      this.quizClick.emit();
    }
  }

  onReturnToTracks(): void {
    this.academyNavigationService.goToAcademyHome();
  }

  get cycleStatus(): 'locked' | 'locked-prerequisites' | 'signed-up' | 'passed' | 'passed-can-retry' | 'passed-participating' | 'available' | null {
    if (!this.courseTrack?.has_practice_test_cycle) return null;
    // Levels 1-4
    if (this.courseTrack?.user_has_passed_practice_test_cycle_except_lv_5) return 'passed';
    // Level 5: passed + currently in a new cycle
    if (this.courseTrack?.user_has_passed_practice_test_cycle_lv_5 && this.courseTrack?.user_is_currently_in_lv_5_cycle) return 'passed-participating';
    // Level 5: passed + NOT in a cycle (can retry)
    if (this.courseTrack?.user_has_passed_practice_test_cycle_lv_5) return 'passed-can-retry';

    if (this.courseTrack?.user_has_applied_to_test_cycle && this.courseTrack?.user_is_currently_in_practice_cycle) return 'signed-up';
    if (this.hasQuiz && this.courseTrack?.user_has_passed_quiz !== true) return 'locked';
    if (this.courseTrack?.user_can_apply_to_test_cycle !== true) return 'locked-prerequisites';
    return 'available';
  }

  get cycleLabel(): string {
    switch (this.cycleStatus) {
      case 'locked':
      case 'locked-prerequisites':
        return 'Practice Test Cycle Locked';
      case 'signed-up':
        return 'You Are Signed Up!';
      case 'passed':
      case 'passed-can-retry':
        return 'You Did It!';
      case 'passed-participating':
        return 'You Are Signed Up!';
      case 'available':
        return 'Sign Up to the Cycle!';
      default:
        return '';
    }
  }

  get cycleHint(): string {
    switch (this.cycleStatus) {
      case 'locked':
        return 'Review all lessons & pass the quiz first';
      case 'locked-prerequisites':
        return 'Complete all of the preceding practice cycles first';
      case 'signed-up':
        return 'Complete the cycle requirements to advance';
      case 'passed':
      case 'passed-can-retry':
        return 'You have passed the practice cycle';
      case 'passed-participating':
        return 'Complete the cycle requirements to advance';
      case 'available':
        return 'Practice testing in a live environment';
      default:
        return '';
    }
  }

  get showNextCourse(): boolean {
    if (!this.courseTrack?.user_has_viewed_all_courses) return false;
    if (this.hasQuiz && !this.courseTrack?.user_has_passed_quiz) return false;
    if (hasCompletedLearningTrack(this.courseTrack)) return false;
    return !!this.courseTrack?.next_course_track_id;
  }

  get showReturnToTracks(): boolean {
    if (!this.courseTrack?.user_has_viewed_all_courses) return false;
    if (this.hasQuiz && !this.courseTrack?.user_has_passed_quiz) return false;
    if (hasCompletedLearningTrack(this.courseTrack)) return true;
    return !this.courseTrack?.next_course_track_id;
  }

  onNextCourse(): void {
    this.academyNavigationService.goToNextCourse(this.courseTrack);
  }

  onCycleSignUp(): void {
    this.cycleSignUp.emit();
  }

  onConfirmSignUp(): void {
    this.cycleSignUpConfirm.emit();
  }
}
